import "./styles.scss";

import {
  LessonResponse,
  useTrialLesson,
} from "../../context/TrailLessonContext";
import { PenIcon, SquarePenIcon } from "lucide-react";
import React, { useEffect, useState } from "react";
import { currencyFormat, isScreenLarge } from "../../utils/helpers";
import { deleteHandler, fetchLessons } from "../../features/lessonApi";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { API_URL } from "../../utils/constants";
import AlertMessage from "./trial/AlertMessage";
import { AppLoader } from "../../components/AppLoader";
import Button from "../../components/Button";
import CreateTrialModal from "./trial/TrialLessonModal";
import EditTrialDescriptionModal from "./trial/EditTrialDescriptionModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Layout from "../layout/Portal";
import { LessonModal } from "./Modal";
import TrialLessonPopupModal from "./trial/TrialLessonPopup";
import { toast } from "sonner";
import { useDialog } from "../../context/DialogContext";

interface LessonState {
  error: string;
  data: any;
  loading: boolean;
}
interface LanguagesState {
  error: string;
  data: any;
  loading: boolean;
}
interface DeleteLesson {
  error: string;
  data: any;
  loading: boolean;
}

export interface LessonPackage {
  packageId: string;
  quantity: number;
  duration: number;
  price: number;
}

export interface Lesson {
  lessonId: string;
  teacherId: string;
  name: string;
  description: string;
  minPrice: number;
  languageId: number;
  lessonType: "trial" | "standard";
  packages: LessonPackage[];
}

export const Lessons = () => {
  const queryClient = useQueryClient();
  const { openDialog, closeDialog } = useDialog();
  const { isTrialLessonCreated } = useTrialLesson();

  const [state, updateState] = useState<LessonState>({
    error: "",
    data: [],
    loading: false,
  });
  const [languages, updateLanguages] = useState<LanguagesState>({
    error: "",
    data: [],
    loading: false,
  });

  const [deleteLesson, updateDeleteLesson] = useState<DeleteLesson>({
    error: "",
    data: [],
    loading: false,
  });

  const [language, updateLanguage] = useState("");
  const [dynamicData, setDynamicData] = useState([]);
  const [trialLesson, setTrialLesson] = useState<
    LessonResponse["result"] | undefined
  >(undefined);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [start, onStart] = useState(true);
  const {
    data: Lessons,
    isPending: isLoading,
    isError: error,
    isFetching,
  } = useQuery({
    queryKey: ["lessons"],
    queryFn: fetchLessons,
  });

  const [showTrialPopup, setTrialPopup] = useState(false);
  const [showModal, setShowModal] = useState(false);
  // const handleCloseModal = () => setShowModal(false);

  useEffect(() => {
    setTrialPopup(true);
  }, []);

  const handleCreateLesson = () => {
    setTrialPopup(false);
    setShowModal(true);
  };

  useEffect(() => {
    if (Lessons) {
      updateLanguage(Lessons?.languagesTaught[0]?.name);
      setDynamicData(Lessons?.languagesTaught[0]?.lessons);

      // Sett Trail Lesson Obj
      setTrialLesson(Lessons?.trailLesson);

      updateState({
        ...state,
        data: Lessons,
        loading: false,
      });
    }
  }, [Lessons]);

  const fetchLanguage = async () => {
    const token = await localStorage.getItem("token");
    updateLanguages({ ...state, loading: true, error: "" });

    const url = `${API_URL}/languages/languages-taught/`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const result = await response.json();

      if (result) {
        updateLanguages({
          ...state,
          data: result.result,
          loading: false,
        });
      } else {
        updateLanguages({
          ...state,
          loading: false,
          error: result.message ?? "Something went wrong",
        });
      }
    } catch (err: any) {
      updateLanguages({
        ...state,
        loading: false,
        error: err.message ?? "Something went wrong",
      });
    }
  };

  const merchantId = localStorage.getItem("teacherId");

  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<{
    id: string;
    description: string;
  } | null>(null);

  const handleEditTrial = (lesson: LessonResponse["result"]) => {
    setSelectedLesson({ id: lesson.id, description: lesson.description });
    setShowEditModal(true);
  };

  const { mutateAsync: deleteMutation, isPending } = useMutation({
    mutationFn: deleteHandler,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["lessons"] });

      queryClient.invalidateQueries({
        queryKey: ["trialLessonCreated", merchantId],
      });
      queryClient.setQueryData(["trialLessonCreated", merchantId], false);

      toast.success(
        `lesson successfully ${data?.message} ` ||
          "Lesson successfully deleted ",
        { richColors: true },
      );

      closeDialog();
      updateState({
        ...state,
        loading: true,
        error: "",
      });
    },
    onError: (error) => {
      if (error) {
        toast.error(
          error?.message || "something went wrong. Please try again",
          { richColors: true },
        );
        closeDialog();
      }
    },
  });

  const handleDeleteModal = (id: string, lessontitle: string) => {
    console.log(id);
    openDialog({
      dialogType: "Delete",
      title: "Delete",
      content: (
        <div>
          <p>Are you sure you want to delete this lesson</p>
          <p>{lessontitle}</p>
        </div>
      ),
      isConfirm: () => {
        // _deleteHandler(id);
        deleteMutation(id);
      },
    });
  };

  useEffect(() => {
    fetchLessons();
    fetchLanguage();
  }, []);

  const closeModal = () => {
    setIsOpen(false);
  };

  const [isLargeScreen, setIsLargeScreen] = useState(isScreenLarge());

  useEffect(() => {
    const handleResize = () => setIsLargeScreen(isScreenLarge());

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const openModal = () => {
    console.warn(isLargeScreen, isScreenLarge);
    if (isLargeScreen) {
      toast.warning(
        "This action is only available on larger screens. Please switch to a desktop for the best experience.",
        {
          richColors: true,
        },
      );
    } else {
      setIsOpen(true);
    }
  };

  const sortedLessons: Lesson[] = [...(dynamicData ?? [])].sort(
    (a: Lesson, b: Lesson) => {
      if (a.lessonType === "trial" && b.lessonType !== "trial") return -1;
      if (a.lessonType !== "trial" && b.lessonType === "trial") return 1;
      return 0;
    },
  );

  return (
    <Layout color={"transparent"}>
      {!isTrialLessonCreated && (
        <TrialLessonPopupModal
          isOpen={showTrialPopup}
          onClose={() => setTrialPopup(false)}
          onCreate={handleCreateLesson}
        />
      )}

      <div className="lessonsMain">
        <div className="titleContainer">
          <div className="languagesContainer">
            {state.data?.languagesTaught?.map((item: any, index: any) => (
              <div
                key={index}
                className={
                  language === item.name ? "languagePicked" : "language"
                }
                onClick={() => {
                  updateLanguage(item.name);
                  setDynamicData(
                    state.data?.languagesTaught?.filter(
                      (e: any) => e.name === item.name,
                    )[0].lessons,
                  );
                }}
              >
                <p>{item.name}</p>
              </div>
            ))}
          </div>
          {languages.loading ? null : (
            <Button
              variant="secondary"
              onClick={() => openModal()}
              className="createBtn"
            >
              + Create Lesson
            </Button>
          )}
        </div>

        {/* Global warning */}
        {!isTrialLessonCreated && <AlertMessage title="Action Required" />}

        {showModal && (
          <div className="modalBackdrop">
            <div className="modalWrapper">
              <CreateTrialModal handleCloseModal={() => setShowModal(false)} />
            </div>
          </div>
        )}

        {isFetching ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <AppLoader />
          </div>
        ) : state.error ? (
          <div>
            <p>Something went wrong</p>
          </div>
        ) : (
          <div>
            {trialLesson && (
              <>
                <div className="lessonsContainer">
                  <div className={`lessonsEach ${"trialLessonCard"}`}>
                    <div className="lesson">
                      <div className="textSpace">
                        <p className="title">Lesson Name</p>
                        <p className="subTitleText">{trialLesson?.title}</p>
                        <p className="title">Description</p>
                        <p className="subTitleText">
                          {trialLesson?.description}
                        </p>
                      </div>

                      {/* <div className="delIcon">
                        <FontAwesomeIcon
                          className="deleteIcon"
                          icon={["fas", "trash"]}
                          onClick={() =>
                            handleDeleteModal(trialLesson.id, trialLesson.title)
                          }
                        />
                      </div> */}
                      <SquarePenIcon
                        className="editIcon"
                        onClick={() => handleEditTrial(trialLesson)}
                      />
                    </div>

                    <details>
                      <summary className="summary">See Packages</summary>

                      <div className="leftPackageContainer">
                        <div className="eachSession">
                          <p className="title">Quantity</p>
                          <p className="subTitle">1</p>
                        </div>
                        <div className="eachSession">
                          <p className="title">Duration</p>
                          <p className="subTitle">
                            {trialLesson?.duration} Minutes
                          </p>
                        </div>
                        <div className="eachSession">
                          <p className="title">Price</p>
                          <p className="subTitle">
                            {currencyFormat(trialLesson?.price)}
                          </p>
                        </div>
                      </div>
                    </details>
                  </div>
                </div>
              </>
            )}

            {sortedLessons?.length > 0 ? (
              <div className="lessonsContainer">
                {sortedLessons.map((item: Lesson, index: number) => (
                  <div
                    key={index}
                    className={`lessonsEach ${item.lessonType === "trial" ? "trialLessonCard" : ""}`}
                  >
                    <div className="lesson">
                      <div className="textSpace">
                        <p className="title">Lesson Name</p>
                        <p className="subTitleText">{item.name}</p>
                        <p className="title">Description</p>
                        <p className="subTitleText">{item.description}</p>
                      </div>

                      <div className="delIcon">
                        <FontAwesomeIcon
                          className="deleteIcon"
                          icon={["fas", "trash"]}
                          onClick={() =>
                            handleDeleteModal(item.lessonId, item.name)
                          }
                        />
                      </div>
                    </div>

                    <details>
                      <summary className="summary">See Packages</summary>
                      {item.packages?.map((pkg: any, idx: number) => (
                        <div key={idx} className="leftPackageContainer">
                          <div className="eachSession">
                            <p className="title">Quantity</p>
                            <p className="subTitle">{pkg.quantity}</p>
                          </div>
                          <div className="eachSession">
                            <p className="title">Duration</p>
                            <p className="subTitle">{pkg.duration} Minutes</p>
                          </div>
                          <div className="eachSession">
                            <p className="title">Price</p>
                            <p className="subTitle">
                              {currencyFormat(pkg.price)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </details>
                  </div>
                ))}
              </div>
            ) : (
              <div className="noLesson">
                <p>You have not created any lessons.</p>
                <Button
                  variant="secondary"
                  onClick={() => openModal()}
                  className="createBtn"
                >
                  Create Lesson
                </Button>
              </div>
            )}
          </div>
        )}

        <LessonModal
          openModal={openModal}
          closeModal={closeModal}
          modalIsOpen={modalIsOpen}
          languages={languages.data.languages}
          fetchLessons={fetchLessons}
        />

        {showEditModal && selectedLesson && (
          <EditTrialDescriptionModal
            handleCloseModal={() => setShowEditModal(false)}
            lessonId={selectedLesson.id}
            currentDescription={selectedLesson.description}
          />
        )}
      </div>
    </Layout>
  );
};
